import { ArrowRight, Cable, Check, CheckCircle2, Database, Fingerprint, KeyRound, LockKeyhole, Plus, Puzzle, ShieldCheck, TerminalSquare, Trash2 } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useLocation } from "react-router-dom";
import { CODEX_MCP_CONFIG, CODEX_SKILL_PATH, LOOPGROUP_KEY_ENV, claudeCodeInstallCommands } from "../../shared/integrations";
import type { PublicCardSummary } from "../../shared/publicCard";
import { api, formatRelative } from "../api";
import { useAuth } from "../auth";
import { BoundaryCallout, Button, CopyButton, EmptyState, ErrorState, LoadingState, Modal } from "../components";
import { SETTINGS_TABS, settingsTabFromHash, settingsTabHash } from "./settingsTabs";
import { TagVocabulary } from "./TagVocabulary";
import "../styles/administration.css";

type ApiKey = { id: string; name: string; token_prefix?: string; tokenPrefix?: string; scopes: string[]; created_at?: string; createdAt?: string; last_used_at?: string | null; revoked_at?: string | null; kind?: "key" | "connector"; client_name?: string | null; expires_at?: string | null };
type FreshKey = ApiKey & { token: string };
type CredentialState = "loading" | "ready" | "error" | "restricted";
type ApiRequest = <T>(path: string, options?: RequestInit, organizationId?: string | null) => Promise<T>;

export async function requestSettingsKeys(
  organizationId: string,
  request: ApiRequest,
  isCurrent: () => boolean,
  onSuccess: (keys: ApiKey[]) => void,
  onError: () => void,
) {
  try {
    const data = await request<{ keys: ApiKey[] }>("/api/app/keys", {}, organizationId);
    if (!isCurrent()) return;
    onSuccess(data.keys);
  } catch {
    if (!isCurrent()) return;
    onError();
  }
}

const REPORTING_INSTRUCTION = `You are connected to this workspace's LoopGroup. Treat a loop as any recurring or multi-step agent process with an observable outcome.

Before a loop begins, call LoopGroup's report_run tool with a stable external run ID, status "running", the loop name, cadence, start time, and your agent name/provider. When the loop ends, call report_run again with the same external run ID and status "succeeded", "failed", or "cancelled". Include the completion time, duration, a concise outcome summary, any error, token counts, reported cost, and useful numeric metrics when they are actually known. Never invent telemetry.

Before creating a new loop, use list_loops to avoid duplicates. Use get_group_context when another team's current work or ownership could improve the result. Do not make administrative changes unless the user explicitly asks and this key has manage scope.`;

export function SettingsPage() {
  const { activeOrganization, refresh } = useAuth();
  const location = useLocation();
  const tab = settingsTabFromHash(location.hash);
  const [name, setName] = useState(activeOrganization?.name ?? "");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [credentialState, setCredentialState] = useState<CredentialState>("loading");
  const [keyModalOpen, setKeyModalOpen] = useState(false);
  const [freshKey, setFreshKey] = useState<FreshKey | null>(null);
  const [keyActionError, setKeyActionError] = useState("");
  const organizationId = activeOrganization?.id ?? "";
  const organizationName = activeOrganization?.name ?? "";
  const organizationRole = activeOrganization?.role ?? "";
  const canManage = ["owner", "admin"].includes(organizationRole);
  const personal = activeOrganization?.kind === "personal";
  const credentialContextKey = `${organizationId}:${organizationRole}`;
  const [credentialOwnerKey, setCredentialOwnerKey] = useState(credentialContextKey);
  const credentialGeneration = useRef(0);
  const currentCredentialContextKey = useRef(credentialContextKey);
  currentCredentialContextKey.current = credentialContextKey;
  const credentialIsCurrent = credentialOwnerKey === credentialContextKey;
  const visibleKeys = credentialIsCurrent ? keys : [];
  const visibleCredentialState = credentialIsCurrent ? credentialState : canManage ? "loading" : "restricted";
  const exampleToken = freshKey?.token ?? "YOUR_LOOPGROUP_KEY";
  const mcpConfig = useMemo(() => JSON.stringify({ mcpServers: { loopgroup: { type: "http", url: "https://loopgroup.ai/mcp", headers: { Authorization: `Bearer ${exampleToken}` } } } }, null, 2), [exampleToken]);
  const restExample = useMemo(() => `POST https://loopgroup.ai/v1/report\nAuthorization: Bearer ${exampleToken}\nContent-Type: application/json\n\n{\n  "loop": { "externalId": "stable-loop-id", "name": "Loop name", "cadence": "daily" },\n  "run": { "externalId": "stable-run-id", "status": "running", "startedAt": "2026-09-05T14:00:00Z" }\n}`, [exampleToken]);
  const claudeInstall = useMemo(() => claudeCodeInstallCommands(exampleToken), [exampleToken]);

  useEffect(() => {
    setName(organizationName);
    setSaved(false);
    setSaveError("");
    setFreshKey(null);
    setKeyActionError("");
  }, [organizationId, organizationName, organizationRole]);

  useLayoutEffect(() => {
    credentialGeneration.current += 1;
    setCredentialOwnerKey(credentialContextKey);
    setKeys([]);
    setCredentialState(canManage ? "loading" : "restricted");
  }, [canManage, credentialContextKey]);

  const loadKeys = useCallback(async () => {
    if (currentCredentialContextKey.current !== credentialContextKey) return;
    const generation = ++credentialGeneration.current;
    if (!organizationId || !canManage) {
      setCredentialOwnerKey(credentialContextKey);
      setKeys([]);
      setCredentialState("restricted");
      return;
    }
    setCredentialOwnerKey(credentialContextKey);
    setKeys([]);
    setCredentialState("loading");
    await requestSettingsKeys(
      organizationId,
      api,
      () => credentialGeneration.current === generation && currentCredentialContextKey.current === credentialContextKey,
      (nextKeys) => {
        setCredentialOwnerKey(credentialContextKey);
        setKeys(nextKeys);
        setCredentialState("ready");
      },
      () => {
        setCredentialOwnerKey(credentialContextKey);
        setKeys([]);
        setCredentialState("error");
      },
    );
  }, [canManage, credentialContextKey, organizationId]);

  useEffect(() => {
    void loadKeys();
    return () => { credentialGeneration.current += 1; };
  }, [loadKeys]);

  async function save(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setSaved(false);
    setSaveError("");
    try {
      await api("/api/app/group", { method: "PATCH", body: JSON.stringify({ name }) }, activeOrganization?.id);
      await refresh();
      setSaved(true);
    } catch (caught) {
      setSaveError(caught instanceof Error ? caught.message : "These settings could not be saved");
    } finally {
      setBusy(false);
    }
  }

  async function revokeKey(key: ApiKey) {
    if (!window.confirm(`Revoke ${key.name}? Agents using it stop reporting immediately.`)) return;
    setKeyActionError("");
    try {
      await api(`/api/app/keys/${key.id}`, { method: "DELETE" }, activeOrganization?.id);
      await loadKeys();
    } catch (caught) {
      setKeyActionError(caught instanceof Error ? caught.message : `${key.name} could not be revoked`);
    }
  }

  if (!activeOrganization) return <div className="page admin-page settings-page"><LoadingState>Resolving the current workspace…</LoadingState></div>;

  return (
    <div className={`page admin-page settings-page ${personal ? "admin-page--personal" : "admin-page--group"}`}>
      <header className="admin-header">
        <h1>Settings</h1>
        <p>{personal ? "The name, agent keys, public cards, tags, and policies of your private workspace." : "The name, agent keys, public cards, tags, and policies of this group."}</p>
        <p className="admin-header__facts" aria-label="Current settings context">{activeOrganization.name} · {personal ? "private workspace" : "shared group"} · {activeOrganization.role}</p>
      </header>

      <nav className="admin-tabs" aria-label="Settings sections">
        {SETTINGS_TABS.map((item) => (
          <Link key={item.id} to={{ pathname: location.pathname, hash: settingsTabHash(item.id) }} className={tab === item.id ? "is-active" : ""} aria-current={tab === item.id ? "page" : undefined}>{item.label}</Link>
        ))}
      </nav>

      {tab === "workspace" && (
        <section className="panel admin-surface admin-workspace" id="workspace" aria-labelledby="workspace-heading">
          <div className="admin-surface__section">
            <h2 className="eyebrow" id="workspace-heading">Name and ID</h2>
            <form className="form-stack admin-identity-form" onSubmit={(event) => void save(event)}>
              <label><span>{personal ? "Workspace name" : "Group name"}</span><input value={name} onChange={(event) => { setName(event.target.value); setSaved(false); }} disabled={!canManage} required minLength={2} /></label>
              <label><span>{personal ? "Workspace ID" : "Group ID"}</span><input value={activeOrganization.id} disabled className="mono-input" /></label>
              {!canManage && <BoundaryCallout title="Read-only role">An owner or admin controls the name. Your access to the group remains unchanged.</BoundaryCallout>}
              {saveError && <div className="form-error" role="alert">{saveError}</div>}
              {canManage && <div className="admin-save-row"><Button type="submit" busy={busy}>{personal ? "Save workspace" : "Save group"}</Button>{saved && <span className="admin-save-confirmation" role="status"><CheckCircle2 size={15} aria-hidden="true" /> Saved</span>}</div>}
            </form>
          </div>
          <div className="admin-surface__section">
            <h2 className="eyebrow">How access works</h2>
            <div className="admin-trust-list">
              <article><Fingerprint size={16} aria-hidden="true" /><div><h3>People use passkeys</h3><p>No passwords or biometric data are stored by LoopGroup.</p></div></article>
              <article><KeyRound size={16} aria-hidden="true" /><div><h3>Agents use scoped keys</h3><p>Read, report, and manage authority stay separate and revocable.</p></div></article>
              <article><Database size={16} aria-hidden="true" /><div><h3>{personal ? "Sharing is per-loop" : "One shared record"}</h3><p>{personal ? "Groups see only loops with an explicit, reversible grant." : "Human and agent actions meet in one group-authorized record."}</p></div></article>
            </div>
          </div>
        </section>
      )}

      {tab === "keys" && (
        <section className="admin-surface admin-credential-plane receiver" id="keys" aria-labelledby="credential-heading">
          <header className="admin-surface__head">
            <div><h2 className="eyebrow" id="credential-heading">Agent keys</h2><p>Agents receive only the authority named on their key. Complete secret values are shown once at creation, never in this ledger.</p></div>
            {canManage && <Button onClick={() => setKeyModalOpen(true)}><Plus size={15} aria-hidden="true" /> Create API key</Button>}
          </header>

          {freshKey && (
            <div className="admin-fresh-key" role="status">
              <span><Check size={15} aria-hidden="true" /> Key created for {freshKey.name}. This is the only time the complete value is shown.</span>
              <CopyButton value={freshKey.token} label="Copy key" />
              <code>{freshKey.token}</code>
            </div>
          )}

          <div className="admin-scope-strip" aria-label="Credential scope reference">
            <article><span>01</span><h3>Read</h3><p>Group context, loops, runs, and diagnostics.</p></article>
            <article><span>02</span><h3>Report</h3><p>Create and update run telemetry for selected loops.</p></article>
            <article><span>03</span><h3>Manage</h3><p>Change loops, teams, and invitations when explicitly granted.</p></article>
          </div>

          <div className="admin-key-ledger">
            <div className="admin-key-ledger__head" aria-hidden="true"><span>Credential</span><span>Granted scopes</span><span>Activity</span><span /></div>
            {visibleCredentialState === "loading" && <LoadingState>Reading the redacted credential ledger…</LoadingState>}
            {visibleCredentialState === "error" && <ErrorState action={<Button variant="secondary" onClick={() => void loadKeys()}>Try again</Button>}>The credential ledger could not be loaded. No secret material was exposed.</ErrorState>}
            {visibleCredentialState === "restricted" && <div className="admin-restricted-state"><ShieldCheck size={19} aria-hidden="true" /><div><h3>Credential details are permission-limited.</h3><p>Your {activeOrganization.role} role does not list or manage keys. An owner or admin can review the ledger.</p></div></div>}
            {visibleCredentialState === "ready" && visibleKeys.length === 0 && <EmptyState eyebrow="No credentials yet" title="No agent authority has been issued">Create a scoped key when an agent is ready to report. Scanning from Sources creates its own report-only key.</EmptyState>}
            {visibleCredentialState === "ready" && visibleKeys.length > 0 && (
              <div className="admin-key-list">
                {visibleKeys.map((key) => {
                  const prefix = key.token_prefix ?? key.tokenPrefix ?? "Prefix unavailable";
                  const createdAt = key.created_at ?? key.createdAt;
                  // A connector key was minted by an OAuth consent (Claude.ai, ChatGPT); it shows the client instead of a prefix.
                  const connector = key.kind === "connector";
                  return (
                    <article key={key.id}>
                      {connector ? <Cable className="admin-key-icon" size={15} aria-hidden="true" /> : <KeyRound className="admin-key-icon" size={15} aria-hidden="true" />}
                      <div className="admin-key-identity"><strong>{key.name} {connector && <span className="admin-key-chip">connector</span>}</strong>{connector ? <code aria-label={`${key.name} connector client`}>{key.client_name ?? "OAuth client"}{key.expires_at && !key.revoked_at ? ` · renews by ${formatRelative(key.expires_at)}` : ""}</code> : <code aria-label={`${key.name} redacted token prefix`}>{prefix}…</code>}</div>
                      <div className="admin-key-scopes" aria-label={`${key.name} scopes`}>{key.scopes.map((scope) => <span key={scope}>{scope}</span>)}</div>
                      <div className="admin-key-activity"><span className={`admin-state-label ${key.revoked_at ? "admin-state-label--revoked" : "admin-state-label--open"}`}><span aria-hidden="true" />{key.revoked_at ? "revoked" : "active"}</span><small>{key.last_used_at ? `Used ${formatRelative(key.last_used_at)}` : createdAt ? `Created ${formatRelative(createdAt)}` : "Never used"}</small></div>
                      {canManage && !key.revoked_at ? <button className="icon-button admin-danger-action" aria-label={`Revoke ${key.name}`} title={`Revoke ${key.name}`} onClick={() => void revokeKey(key)}><Trash2 size={15} aria-hidden="true" /></button> : <span />}
                    </article>
                  );
                })}
              </div>
            )}
            {keyActionError && <div className="form-error admin-key-error" role="alert">{keyActionError}</div>}
          </div>

          <div className="admin-connect-grid">
            <article className="admin-code">
              <header><span><Cable size={14} aria-hidden="true" /> MCP configuration</span><CopyButton value={mcpConfig} label="Copy" /></header>
              <pre>{mcpConfig}</pre>
              <p>Streamable HTTP. The bearer key decides which workspace the agent sees and which operations it may use.</p>
            </article>
            <article className="admin-code">
              <header><span><TerminalSquare size={14} aria-hidden="true" /> REST reporting</span><CopyButton value={restExample} label="Copy" /></header>
              <pre>{restExample}</pre>
              <p>For agents without MCP. The same external run ID updates idempotently from start to terminal status.</p>
            </article>
          </div>
          <div className="admin-plugin-install">
            <header><span><Puzzle size={14} aria-hidden="true" /> Install as a plugin</span></header>
            <p>The plugin ships the same scan, activation, categorize, handoff, and reporting prompts as slash commands, reading the key from <code>{LOOPGROUP_KEY_ENV}</code> instead of a pasted prompt.</p>
            <div className="admin-connect-grid">
              <article className="admin-code">
                <header><span><TerminalSquare size={14} aria-hidden="true" /> Claude Code</span><CopyButton value={claudeInstall} label="Copy" /></header>
                <pre>{claudeInstall}</pre>
                <p>Run the three commands, then restart Claude Code. The commands appear as /loopgroup:scan, activate, categorize, handoff, and report.</p>
              </article>
              <article className="admin-code">
                <header><span><TerminalSquare size={14} aria-hidden="true" /> Codex</span><CopyButton value={CODEX_MCP_CONFIG} label="Copy" /></header>
                <pre>{CODEX_MCP_CONFIG}</pre>
                <p>Append to ~/.codex/config.toml, export {LOOPGROUP_KEY_ENV} in your shell, and copy integrations/codex/skills/loopgroup from the LoopGroup repository to {CODEX_SKILL_PATH}.</p>
              </article>
            </div>
          </div>
          <article className="admin-code admin-code--prompt">
            <header><span><KeyRound size={14} aria-hidden="true" /> Reporting instruction for the agent</span><CopyButton value={REPORTING_INSTRUCTION} label="Copy instruction" /></header>
            <pre>{REPORTING_INSTRUCTION}</pre>
            <p>Paste into the agent's instructions once a key is configured. Only explicitly instrumented loops send start and terminal outcomes; a key alone proves nothing is running.</p>
          </article>

          <footer><LockKeyhole size={15} aria-hidden="true" /><p>{personal ? "Creating or claiming a catalog does not install an observer, enable run reporting, or share a loop." : "Group membership does not grant agent authority. Keys remain separate, scoped, and revocable."}</p></footer>
        </section>
      )}

      {tab === "cards" && <PublicCardsPanel organizationId={organizationId} />}

      {tab === "tags" && <TagVocabulary organizationId={organizationId} canManage={canManage} />}

      {tab === "policies" && (
        <section className="panel admin-surface admin-policies" id="policies" aria-labelledby="policies-heading">
          <header className="admin-surface__head"><div><h2 className="eyebrow" id="policies-heading">Policies and updates</h2><p>What LoopGroup records, what it promises, and what changed.</p></div></header>
          <nav className="admin-policy-list" aria-label="Policies and updates">
            <Link to="/privacy"><span>Privacy</span><small>What is stored and what never leaves the agent</small><ArrowRight size={14} aria-hidden="true" /></Link>
            <Link to="/terms"><span>Terms</span><small>The agreement for using LoopGroup</small><ArrowRight size={14} aria-hidden="true" /></Link>
            <Link to="/changelog"><span>Changelog</span><small>What changed, in order</small><ArrowRight size={14} aria-hidden="true" /></Link>
          </nav>
          <footer className="admin-legal"><span>© 2026 LoopGroup</span></footer>
        </section>
      )}

      <KeyModal open={keyModalOpen} onClose={() => setKeyModalOpen(false)} onCreated={(key) => { setFreshKey(key); setKeyModalOpen(false); void loadKeys(); }} />
    </div>
  );
}

function kindLabel(kind: PublicCardSummary["kind"]): string {
  return kind === "loop" ? "Loop" : kind === "collection" ? "Collection" : "Status";
}

/** The public links published from this workspace. Links are shown once at creation; here they can only be revoked. */
function PublicCardsPanel({ organizationId }: { organizationId: string }) {
  const [cards, setCards] = useState<PublicCardSummary[]>([]);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [actionError, setActionError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!organizationId) return;
    const controller = new AbortController();
    setState("loading");
    setActionError("");
    void api<{ cards: PublicCardSummary[] }>("/api/app/cards", { signal: controller.signal }, organizationId)
      .then((data) => { setCards(data.cards); setState("ready"); })
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === "AbortError") return;
        setState("error");
      });
    return () => controller.abort();
  }, [organizationId, refreshKey]);

  async function revoke(card: PublicCardSummary) {
    if (!window.confirm(`Revoke the public card "${card.title}"? Its link, image, and badge stop working immediately.`)) return;
    setActionError("");
    try {
      await api(`/api/app/cards/${card.id}`, { method: "DELETE" }, organizationId);
      setRefreshKey((value) => value + 1);
    } catch (caught) {
      setActionError(caught instanceof Error ? caught.message : "The card could not be revoked.");
    }
  }

  const active = cards.filter((card) => !card.revokedAt).length;

  return (
    <section className="panel admin-surface admin-cards-panel" id="cards" aria-labelledby="cards-heading">
      <header className="admin-surface__head">
        <div><h2 className="eyebrow" id="cards-heading">Public cards</h2><p>A public card shows loop names, provider, cadence, evidence stage, reliability with its sample, and a seven-day rhythm. Links are shown once when a card is created. Revoking a card turns its page, image, and badge into a 404.</p></div>
        {state === "ready" && <span className="admin-surface__count">{active} active {active === 1 ? "link" : "links"}</span>}
      </header>
      {state === "loading" && <LoadingState>Reading published cards…</LoadingState>}
      {state === "error" && <ErrorState action={<Button variant="secondary" onClick={() => setRefreshKey((value) => value + 1)}>Try again</Button>}>The card list could not be loaded.</ErrorState>}
      {state === "ready" && cards.length === 0 && <EmptyState eyebrow="Nothing published" title="No public cards yet">Share a loop from its detail page, or a status card from Pulse, and it will be listed here.</EmptyState>}
      {state === "ready" && cards.length > 0 && (
        <div className="admin-card-list">
          <div className="admin-card-list__head" aria-hidden="true"><span>Card</span><span>Kind</span><span>Created</span><span>Views</span><span /></div>
          {cards.map((card) => (
            <article key={card.id} className={card.revokedAt ? "is-revoked" : ""}>
              <div className="admin-card-identity"><strong>{card.title}</strong><code aria-label={`${card.title} link prefix`}>{card.tokenPrefix}… · {card.loopCount} {card.loopCount === 1 ? "loop" : "loops"}{card.showPurpose ? " · purpose shown" : ""}</code></div>
              <span className="admin-card-kind">{kindLabel(card.kind)}</span>
              <span className="admin-card-created">{card.revokedAt ? `Revoked ${formatRelative(card.revokedAt)}` : `Created ${formatRelative(card.createdAt)}`}</span>
              <span className="admin-card-views"><strong>{card.viewCount}</strong> {card.viewCount === 1 ? "view" : "views"}</span>
              {card.revokedAt ? <span className="admin-state-label admin-state-label--revoked"><span aria-hidden="true" />revoked</span> : <button className="icon-button admin-danger-action" aria-label={`Revoke ${card.title}`} title={`Revoke ${card.title}`} onClick={() => void revoke(card)}><Trash2 size={15} aria-hidden="true" /></button>}
            </article>
          ))}
        </div>
      )}
      {actionError && <div className="form-error admin-card-error" role="alert">{actionError}</div>}
    </section>
  );
}

function KeyModal({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: (key: FreshKey) => void }) {
  const { activeOrganization } = useAuth();
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState(["read", "report"]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) { setName(""); setScopes(["read", "report"]); setError(""); }
  }, [open]);

  function toggle(scope: string) {
    setScopes((current) => current.includes(scope) ? current.filter((item) => item !== scope) : [...current, scope]);
  }

  async function create(event: FormEvent) {
    event.preventDefault();
    if (!scopes.length) { setError("Choose at least one scope"); return; }
    setBusy(true);
    setError("");
    try {
      const result = await api<{ key: FreshKey }>("/api/app/keys", { method: "POST", body: JSON.stringify({ name, scopes }) }, activeOrganization?.id);
      onCreated(result.key);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not create key");
    } finally {
      setBusy(false);
    }
  }

  const scopeOptions = [
    { scope: "read", label: "Read", detail: "Group context, loops, runs, diagnostics" },
    { scope: "report", label: "Report", detail: "Create and update run telemetry" },
    { scope: "manage", label: "Manage", detail: "Change loops, teams, and invitations" },
  ];

  return (
    <Modal open={open} title="Create an API key" onClose={onClose}>
      <form className="form-stack admin-modal-form" onSubmit={(event) => void create(event)}>
        <label><span>Key name</span><input value={name} onChange={(event) => setName(event.target.value)} placeholder="Customer signal agent" required minLength={2} /></label>
        <fieldset>
          <legend>Scopes</legend>
          {scopeOptions.map((item) => (
            <label className={`scope-choice ${scopes.includes(item.scope) ? "scope-choice--selected" : ""}`} key={item.scope}>
              <input type="checkbox" checked={scopes.includes(item.scope)} onChange={() => toggle(item.scope)} />
              <span><b>{item.label}</b><small>{item.detail}</small></span>
              {scopes.includes(item.scope) && <Check size={15} aria-hidden="true" />}
            </label>
          ))}
        </fieldset>
        {error && <div className="form-error" role="alert">{error}</div>}
        <div className="admin-action-note"><ShieldCheck size={15} aria-hidden="true" /><p>Start narrow. You can create a separate manage key later.</p></div>
        <div className="modal__actions"><Button type="button" variant="ghost" onClick={onClose}>Cancel</Button><Button type="submit" busy={busy}>Create key</Button></div>
      </form>
    </Modal>
  );
}
